import { Box, Chip, Stack } from "@mui/material";
import React, { useEffect, useState } from "react";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import { adminApiService } from "../../../utils/api";
import {
  GfImage,
  GfImageTag,
  MuiFilterRequestDto,
} from "@swagger/typescript-fetch-goflyer";

import { GridFilterModel, GridRowSelectionModel } from "@mui/x-data-grid";
import { MuiConfirmationModal } from "../../components/MuiConfirmationModal";
import { useTranslation } from "react-i18next";

import { messages } from "../../../locales/messages";
import { useNavigate } from "react-router-dom";
import { toastError, toastSuccess } from "../../../utils/pageUtil/toast";
import { TableData } from "../../../utils/interface/tableData";
import { AccountCardWrapper } from "../../components/AccountCardWrapper";
import { useImagesTableColumns } from "./useImagesTableColumns";
import { SetupPageTopAppBar } from "../../../utils/commonStyledComponents/commonStyled";
import { BlackFontTransparentButton } from "../../../utils/commonStyledComponents/commonMuiStyled";
import {
  fetchDeletedImages,
  restoreImages,
} from "../../../utils/softDeletedImages";
import {
  LeftButtonSection,
  TableWrapper,
  Container,
  FilterLabel,
  ToolbarControls,
  FilterControls,
  FilterInputWrapper,
  SearchInputWrapper,
} from "./styled";
import { PermissionComponentWrapper } from "../../components/PermissionComponentWrapper";
import { useLoading } from "../../../contextApi/LoadingContext";
import { SearchInput } from "../../components/SearchInputField";
import { useAuthHeaders } from "../../../hooks/useAuthHeaders";
import DeleteIcon from "@mui/icons-material/Delete";
import LocalOfferIcon from "@mui/icons-material/LocalOffer";
import ClearIcon from "@mui/icons-material/Clear";
import { useQueryParams } from "../../../hooks/useQueryParams";
import { ListTableQueryPagination } from "../../components/ListTable/ListTableQueyPagination";
import { useImageTagOptions } from "../../../hooks/useImageTagOptions";
import { CustomAutocompleteMultiSelect } from "../../components/CustomAutocomplete";
import { routes } from "../../../utils/constant/navigationRoutes";
import { muiFilterModelHasActiveItems } from "../../../utils/muiGridFilterModel";
import { useLatestRequestGuard } from "../../../hooks/useLatestRequestGuard";
import { guardedListFetch } from "../../../utils/guardedListFetch";

const ImagesPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authHeaders = useAuthHeaders();

  const compactActionButtonSx = {
    width: { xs: "100%", sm: "auto" },
    minWidth: { xs: 0, sm: "auto" },
    justifyContent: { xs: "center", sm: "flex-start" },
    px: { xs: 1, sm: 1.25 },
    py: 0.5,
    fontSize: { xs: 14, sm: 15, md: 16 },
    lineHeight: 1.2,
    "& .MuiButton-startIcon": {
      mx: { xs: 0, sm: undefined },
    },
  };

  const {
    paginationModel,
    sortModel,
    searchText,
    setQueryParams,
    handleTableChange,
  } = useQueryParams(30);

  const [paginationLoading, setPaginationLoading] = useState(false);
  const listRequestGuard = useLatestRequestGuard();
  const [imagesListData, setImagesListData] = useState<TableData<GfImage>>();
  const [open, setOpen] = useState(false);
  const [imageToBeDeleted, setImageToBeDeleted] = useState<GfImage>();
  const { showLoadingScreen } = useLoading();
  const [showDeleted, setShowDeleted] = useState(false);
  const [rowSelectionModel, setRowSelectionModel] =
    useState<GridRowSelectionModel>([]);

  const [batchTagModalOpen, setBatchTagModalOpen] = useState(false);
  const [batchTagTargetIds, setBatchTagTargetIds] = useState<string[]>([]);
  const [selectedTagsForBatch, setSelectedTagsForBatch] = useState<
    GfImageTag[]
  >([]);
  const { imageTagOptionList } = useImageTagOptions();

  const [singleTagEditModalOpen, setSingleTagEditModalOpen] = useState(false);
  const [imageForTagEdit, setImageForTagEdit] = useState<GfImage>();
  const [selectedTagsForSingle, setSelectedTagsForSingle] = useState<
    GfImageTag[]
  >([]);

  const [filterTags, setFilterTags] = useState<GfImageTag[]>([]);
  const [filterModel, setFilterModel] = useState<GridFilterModel>({
    items: [],
  });

  const handleOpenDelete = (value: GfImage) => {
    setImageToBeDeleted(value);
    setOpen(true);
  };

  const handleOpenBulkDelete = () => {
    setOpen(true);
  };

  const handleOpenBatchTagUpdate = () => {
    setBatchTagTargetIds(rowSelectionModel as string[]);
    setRowSelectionModel([]);
    setSelectedTagsForBatch([]);
    setBatchTagModalOpen(true);
  };

  const handleOpenSingleTagEdit = (image: GfImage) => {
    setImageForTagEdit(image);
    setSelectedTagsForSingle(image.imageTags ?? []);
    setSingleTagEditModalOpen(true);
  };

  const handleCancelSingleTagEdit = () => {
    setSingleTagEditModalOpen(false);
    setImageForTagEdit(undefined);
    setSelectedTagsForSingle([]);
  };

  const handleSingleTagUpdate = async () => {
    if (!imageForTagEdit?.id) return;
    try {
      showLoadingScreen(true);
      await adminApiService.updateOneBaseGfImageAdminControllerGfImage(
        { id: imageForTagEdit.id, imageTags: selectedTagsForSingle },
        imageForTagEdit.id,
        authHeaders,
      );
      refreshImagesList();
      showLoadingScreen(false);
      handleCancelSingleTagEdit();
      toastSuccess(t(messages.Successfully_Updated()));
    } catch (error) {
      console.log("error", error);
      showLoadingScreen(false);
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    }
  };

  const handleCancelBatchTagUpdate = () => {
    setBatchTagModalOpen(false);
    setBatchTagTargetIds([]);
    setSelectedTagsForBatch([]);
  };

  const handleBatchUpdateTags = async () => {
    try {
      showLoadingScreen(true);
      await Promise.all(
        batchTagTargetIds.map((id) =>
          adminApiService.updateOneBaseGfImageAdminControllerGfImage(
            { id, imageTags: selectedTagsForBatch },
            id,
            authHeaders,
          ),
        ),
      );
      refreshImagesList();
      showLoadingScreen(false);
      handleCancelBatchTagUpdate();
      toastSuccess(t(messages.Successfully_Updated()));
    } catch (error) {
      console.log("error", error);
      showLoadingScreen(false);
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    }
  };

  const columns = useImagesTableColumns({
    handleOnDelete: handleOpenDelete,
    handleOnEditTags: handleOpenSingleTagEdit,
  });

  const handleCancel = () => {
    setOpen(false);
    setImageToBeDeleted(undefined);
  };

  const fetchImagesListWithFilter = async (
    activeFilterModel: GridFilterModel,
  ) => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () => {
        const body: MuiFilterRequestDto = {
          filterModel: {
            items: activeFilterModel.items.map((item) => ({
              field: item.field,
              operator: item.operator,
              value: item.value,
              id: item.id,
            })),
            logicOperator: activeFilterModel.logicOperator as any,
            quickFilterValues: activeFilterModel.quickFilterValues,
            quickFilterLogicOperator:
              activeFilterModel.quickFilterLogicOperator as any,
          },
          page: paginationModel.page,
          pageSize: paginationModel.pageSize,
          sortModel: sortModel?.map((s) => ({
            field: s.field,
            sort: s.sort as any,
          })),
        };
        return adminApiService.gfImageAdminControllerFilterWithMuiGrid(
          body,
          authHeaders,
        );
      },
      (response) => {
        if (!response) return;
        setImagesListData((prev) => ({
          ...prev,
          rows: response.rows,
          total: response.total,
        }));
      },
    );
  };

  const refreshImagesList = () => {
    if (showDeleted) {
      fetchDeletedImagesList();
    } else if (muiFilterModelHasActiveItems(filterModel)) {
      fetchImagesListWithFilter(filterModel);
    } else if (searchText?.length) {
      searchImageFn(searchText);
    } else {
      fetchImagesList();
    }
  };

  const handleFilterModelChange = (newFilterModel: GridFilterModel) => {
    setFilterModel(newFilterModel);
  };

  const fetchImagesList = async () => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () =>
        adminApiService.gfImageAdminControllerBrowseImages(
          sortModel?.length
            ? `${sortModel[0].field},${sortModel[0]?.sort?.toUpperCase()}`
            : `updatedAt,DESC`,
          filterTags.length ? filterTags.map((t) => t.id) : undefined,
          paginationModel?.pageSize,
          paginationModel?.page,
          authHeaders,
        ),
      (imagesList) => {
        setImagesListData((prev) => ({
          ...prev,
          rows: imagesList.data,
          total: imagesList?.total,
        }));
      },
    );
  };

  const searchImageFn = async (searchValue: string) => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () =>
        adminApiService.gfImageAdminControllerSearchImageWithFilters(
          searchValue,
          paginationModel.page,
          paginationModel.pageSize,
          undefined,
          ["/flyers/", "flyers", "/menus/", "menus"],
          undefined,
          undefined,
          undefined,
          [],
          undefined,
          authHeaders,
        ),
      (response) => {
        if (response.data) {
          setImagesListData((prev) => ({
            ...prev,
            rows: response.data,
            total: response.total,
          }));
        }
      },
    );
  };

  const accessToken = authHeaders.headers.Authorization?.replace(
    /^Bearer\s+/,
    "",
  );

  const fetchDeletedImagesList = async () => {
    await guardedListFetch(
      listRequestGuard,
      setPaginationLoading,
      async () =>
        fetchDeletedImages(
          paginationModel.page,
          paginationModel.pageSize,
          accessToken,
          sortModel?.length
            ? `${sortModel[0].field},${sortModel[0]?.sort?.toUpperCase()}`
            : "updatedAt,DESC",
        ),
      (response) => {
        if (!response) return;
        setImagesListData((prev) => ({
          ...prev,
          rows: response.data,
          total: response.total,
        }));
      },
    );
  };

  const handleToggleDeletedView = () => {
    setRowSelectionModel([]);
    setShowDeleted((prev) => !prev);
  };

  const handleRestoreImages = async () => {
    if (!rowSelectionModel?.length) return;
    try {
      showLoadingScreen(true);
      await restoreImages(rowSelectionModel as string[], accessToken);
      setRowSelectionModel([]);
      await fetchDeletedImagesList();
      showLoadingScreen(false);
      toastSuccess(t(messages.Successfully_Updated()));
    } catch (error) {
      console.log("error", error);
      showLoadingScreen(false);
      toastError(t(messages.There_is_an_error_please_Contact_Administrator()));
    }
  };

  const handleDeleteImage = async () => {
    if (imageToBeDeleted || rowSelectionModel?.length) {
      try {
        showLoadingScreen(true);
        await adminApiService.gfImageAdminControllerSoftDelete(
          {
            ids: [
              ...(imageToBeDeleted?.id ? [imageToBeDeleted.id] : []),
              ...rowSelectionModel,
            ] as string[],
          },
          authHeaders,
        );

        refreshImagesList();

        showLoadingScreen(false);
        handleCancel();
        toastSuccess(t(messages.Successfully_Deleted()));
      } catch (error) {
        console.log("error", error);
        showLoadingScreen(false);
        toastError(
          t(messages.There_is_an_error_please_Contact_Administrator()),
        );
      }
    }
  };

  const isShowingSearchResults =
    !showDeleted &&
    !muiFilterModelHasActiveItems(filterModel) &&
    Boolean(searchText?.length);

  useEffect(() => {
    const hasActiveFilters = muiFilterModelHasActiveItems(filterModel);
    if (showDeleted) {
      fetchDeletedImagesList();
    } else if (hasActiveFilters) {
      fetchImagesListWithFilter(filterModel);
    } else if (searchText?.length) {
      searchImageFn(searchText);
    } else {
      fetchImagesList();
    }
  }, [
    searchText,
    paginationModel,
    sortModel,
    filterTags,
    filterModel,
    showDeleted,
  ]);

  const handleOnSearch = async (value: string) => {
    setQueryParams({
      searchText: value,
      page: 1,
      sortField: null,
      sortOrder: null,
    });
  };

  return (
    <Container data-testid="image-page-test-id">
      <AccountCardWrapper title={t(messages.Image())}>
        <Box data-testid="view-page">
          <SetupPageTopAppBar>
            <LeftButtonSection>
              <Stack
                direction="row"
                spacing={2}
                useFlexGap
                flexWrap="wrap"
                sx={{
                  width: "100%",
                  alignItems: "center",
                  "& > *": {
                    flex: { xs: "1 1 0", sm: "0 0 auto" },
                    minWidth: 0,
                  },
                }}
              >
                <PermissionComponentWrapper
                  requiredPermission={["create"]}
                  requiredPermissionGroup={["image"]}
                  wrapperCss="width: 100%; @media (min-width: 600px) { width: auto; }"
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <UploadFileIcon
                        style={{ color: "#ff8038", fontSize: 25 }}
                      />
                    }
                    onClick={() => {
                      navigate(routes.image.create);
                    }}
                    data-testid="create-button"
                    sx={compactActionButtonSx}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: { xs: "none", sm: "inline" },
                        fontSize: { sm: 14, md: 15 },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t(messages.Create())}
                    </Box>
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>
                <PermissionComponentWrapper
                  requiredPermission={["delete"]}
                  requiredPermissionGroup={["image"]}
                  wrapperCss="width: 100%; @media (min-width: 600px) { width: auto; }"
                >
                  <BlackFontTransparentButton
                    variant="text"
                    onClick={handleToggleDeletedView}
                    data-testid="toggle-deleted-view-button"
                    sx={compactActionButtonSx}
                  >
                    <Box
                      component="span"
                      sx={{
                        fontSize: { sm: 14, md: 15 },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {showDeleted
                        ? t(messages.Images())
                        : t(messages.Deleted())}
                    </Box>
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>
                {showDeleted && (
                  <PermissionComponentWrapper
                    requiredPermission={["delete"]}
                    requiredPermissionGroup={["image"]}
                    wrapperCss="width: 100%; @media (min-width: 600px) { width: auto; }"
                  >
                    <BlackFontTransparentButton
                      variant="text"
                      disabled={!rowSelectionModel?.length}
                      onClick={handleRestoreImages}
                      data-testid="restore-button"
                      sx={compactActionButtonSx}
                    >
                      <Box
                        component="span"
                        sx={{
                          fontSize: { sm: 14, md: 15 },
                          whiteSpace: "nowrap",
                          opacity: !rowSelectionModel?.length ? 0.5 : 1,
                        }}
                      >
                        {t(messages.Restore())}
                      </Box>
                    </BlackFontTransparentButton>
                  </PermissionComponentWrapper>
                )}
                <PermissionComponentWrapper
                  requiredPermission={["delete"]}
                  requiredPermissionGroup={["image"]}
                  wrapperCss="width: 100%; @media (min-width: 600px) { width: auto; }"
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <DeleteIcon
                        style={{
                          color: "#ff8038",
                          fontSize: 25,
                          opacity: !rowSelectionModel?.length ? 0.5 : 1,
                        }}
                      />
                    }
                    disabled={!rowSelectionModel?.length || showDeleted}
                    onClick={handleOpenBulkDelete}
                    data-testid="delete-button"
                    sx={compactActionButtonSx}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: { xs: "none", sm: "inline" },
                        fontSize: { sm: 14, md: 15 },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t(messages.Delete())}
                    </Box>
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>
                <PermissionComponentWrapper
                  requiredPermission={["edit"]}
                  requiredPermissionGroup={["image"]}
                  wrapperCss="width: 100%; @media (min-width: 600px) { width: auto; }"
                >
                  <BlackFontTransparentButton
                    variant="text"
                    startIcon={
                      <LocalOfferIcon
                        style={{
                          color: "#ff8038",
                          fontSize: 25,
                          opacity: !rowSelectionModel?.length ? 0.5 : 1,
                          marginRight: "5px",
                        }}
                      />
                    }
                    disabled={!rowSelectionModel?.length}
                    onClick={handleOpenBatchTagUpdate}
                    data-testid="batch-update-tags-button"
                    sx={compactActionButtonSx}
                  >
                    <Box
                      component="span"
                      sx={{
                        display: { xs: "none", sm: "inline" },
                        fontSize: { sm: 14, md: 15 },
                        whiteSpace: "nowrap",
                      }}
                    >
                      {t(messages.Batch_Update_Tags())}
                    </Box>
                  </BlackFontTransparentButton>
                </PermissionComponentWrapper>
              </Stack>
            </LeftButtonSection>
            <ToolbarControls>
              <FilterControls>
                <FilterLabel>{t(messages.ImageTag())}:</FilterLabel>
                <FilterInputWrapper>
                  <CustomAutocompleteMultiSelect<GfImageTag>
                    optionsList={imageTagOptionList?.map((tag) => ({
                      label: tag.name,
                      value: tag,
                    }))}
                    selectedValues={filterTags.map((tag) => ({
                      label: tag.name,
                      value: tag,
                    }))}
                    handleOnChange={(values) => {
                      setFilterTags((values as GfImageTag[]) ?? []);
                      setQueryParams({ page: 1 });
                    }}
                    WrapperCss="width: 100%; min-width: 0;"
                    inputCss="padding: 3px 6px;"
                  />
                </FilterInputWrapper>
                {filterTags.length > 0 && (
                  <Chip
                    label={t(messages.Cancel())}
                    size="small"
                    icon={<ClearIcon style={{ fontSize: 14 }} />}
                    onClick={() => {
                      setFilterTags([]);
                      setQueryParams({ page: 1 });
                    }}
                    sx={{
                      backgroundColor: "#fff3ec",
                      color: "#ff8038",
                      border: "1px solid #ff8038",
                      fontWeight: 500,
                      flexShrink: 0,
                      alignSelf: { xs: "flex-start", sm: "center" },
                      "& .MuiChip-icon": { color: "#ff8038" },
                      "&:hover": { backgroundColor: "#ffe5d5" },
                    }}
                  />
                )}
              </FilterControls>
              <SearchInputWrapper>
                <SearchInput
                  onSearch={handleOnSearch}
                  initialValue={searchText}
                  dataTestId="image-search-input"
                />
              </SearchInputWrapper>
            </ToolbarControls>
          </SetupPageTopAppBar>

          <TableWrapper>
            <ListTableQueryPagination
              loadFailed={listRequestGuard.loadFailed}
              sortingMode={isShowingSearchResults ? "client" : "server"}
              columns={columns}
              rows={imagesListData?.rows}
              total={paginationLoading ? 0 : imagesListData?.total}
              rowSelectionModel={rowSelectionModel}
              paginationModel={paginationModel}
              sortModel={sortModel}
              isLoading={paginationLoading}
              onTableChange={handleTableChange}
              dynamicHeightAdjuster={230}
              handleRowSelection={(value) => {
                setRowSelectionModel(value);
              }}
              filterModel={filterModel}
              onFilterModelChange={handleFilterModelChange}
            />
          </TableWrapper>

          <MuiConfirmationModal
            open={open}
            handleCancel={handleCancel}
            handleConfirm={handleDeleteImage}
            title={`${t(messages.Delete_Confirmation())}`}
            description={`
        ${t(messages.Are_you_sure_you_want_to_delete_These())} ${t(
          messages.Selected_Images(),
          { count: rowSelectionModel.length },
        )}?  ${t(messages.Image_soft_delete_notice())}
      `}
          />

          <MuiConfirmationModal
            open={batchTagModalOpen}
            handleCancel={handleCancelBatchTagUpdate}
            handleConfirm={handleBatchUpdateTags}
            title={t(messages.Batch_Update_Tags())}
            confirmButtonColor="#ff8038"
            sx={{
              "& .MuiDialog-paper": {
                minWidth: { xs: "auto", sm: 560 },
                minHeight: 360,
              },
            }}
            description={
              <CustomAutocompleteMultiSelect<GfImageTag>
                title={t(messages.ImageTag())}
                optionsList={imageTagOptionList?.map((tag) => ({
                  label: tag.name,
                  value: tag,
                }))}
                selectedValues={selectedTagsForBatch.map((tag) => ({
                  label: tag.name,
                  value: tag,
                }))}
                handleOnChange={(values) =>
                  setSelectedTagsForBatch((values as GfImageTag[]) ?? [])
                }
              />
            }
          />

          <MuiConfirmationModal
            open={singleTagEditModalOpen}
            handleCancel={handleCancelSingleTagEdit}
            handleConfirm={handleSingleTagUpdate}
            title={t(messages.ImageTag())}
            confirmButtonColor="#ff8038"
            sx={{
              "& .MuiDialog-paper": {
                minWidth: { xs: "auto", sm: 560 },
                minHeight: 360,
              },
            }}
            description={
              <CustomAutocompleteMultiSelect<GfImageTag>
                title={t(messages.ImageTag())}
                optionsList={imageTagOptionList?.map((tag) => ({
                  label: tag.name,
                  value: tag,
                }))}
                selectedValues={selectedTagsForSingle.map((tag) => ({
                  label: tag.name,
                  value: tag,
                }))}
                handleOnChange={(values) =>
                  setSelectedTagsForSingle((values as GfImageTag[]) ?? [])
                }
              />
            }
          />
        </Box>
      </AccountCardWrapper>
    </Container>
  );
};

export default ImagesPage;
